import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { validateRoleForDomain } from "@/lib/domain";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);       // user object or null
  const [loading, setLoading] = useState(true); // initial check

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("payflex_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Validate role belongs on this domain
      const check = validateRoleForDomain(data.role);
      if (!check.allowed) {
        localStorage.removeItem("payflex_token");
        setUser(null);
        if (check.redirect) window.location.href = check.redirect;
        return;
      }
      setUser(data);
    } catch {
      localStorage.removeItem("payflex_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const handleAuthResponse = (data) => {
    // Validate role belongs on this domain before saving token
    const check = validateRoleForDomain(data.user.role);
    if (!check.allowed) {
      const msg = check.redirect && check.redirect.includes("dev.")
        ? "Admin accounts must login at dev.flexpay.today"
        : "Partner accounts must login at flexpay.today";
      throw new Error(msg);
    }
    localStorage.setItem("payflex_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    return handleAuthResponse(data);
  };

  const loginPassword = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    return handleAuthResponse(data);
  };

  const requestOtp = async (mobile) => {
    const { data } = await api.post("/auth/otp/request", { mobile });
    return data;
  };

  const verifyOtp = async (mobile, otp) => {
    const { data } = await api.post("/auth/otp/verify", { mobile, otp });
    return handleAuthResponse(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("payflex_token");
    setUser(null);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signup,
        loginPassword,
        requestOtp,
        verifyOtp,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
